import type { ActionOutcome, DraftAction, PublicState, Role, TableState } from '../types.ts';
import { diag } from '../content/compendium.ts';
import { emit, freeBedIndex, nextStep, seatedRoles, seatOfBed, type Ctx } from './common.ts';
import { ventsFree } from './act.ts';
import { resolveAction } from './resolve-drafts.ts';

/** Resolve the receiving service for a known transfer intention. */
export function transferTarget(pub: PublicState, action: DraftAction): Role | null {
  if (action.cmd.type === 'REFER') return action.cmd.to;
  if (action.cmd.type !== 'STEP_DOWN') return null;
  const p = pub.patients[action.cmd.p];
  const step = p ? nextStep(p) : null;
  return step?.kind === 'STEP_DOWN' && (step.bed === 'SW' || step.bed === 'MW') ? seatOfBed(step.bed) : null;
}

/** Reserve consented transfers using actual remaining capacity. */
export function commitTransfers(ctx: Ctx, t: TableState): ActionOutcome[] {
  const roles = seatedRoles(t);
  const offset = (ctx.s.clock.turn - 1) % roles.length;
  const order = [...roles.slice(offset), ...roles.slice(0, offset)];
  let pending = order.flatMap(role => (t.drafts[role]?.actions ?? []).filter(a => a.cmd.type === 'REFER' || a.cmd.type === 'STEP_DOWN').map(action => ({ role, action })));
  const outcomes: ActionOutcome[] = [];
  const limit = pending.length;
  for (let pass = 0; pass < limit && pending.length; pass++) {
    const before = pending.length;
    pending = pending.filter(({ role, action }) => {
      const to = transferTarget(ctx.s, action);
      const consent = t.consents.find(c => c.sender === role && c.receiver === to && c.actionId === action.id && c.offerRevision === action.revision);
      const p = 'p' in action.cmd && action.cmd.p ? ctx.s.patients[action.cmd.p] : undefined;
      const cancel = t.timeOut.turn === ctx.s.clock.turn;
      if (cancel || !p || p.status !== 'ACTIVE' || !to || !consent) {
        outcomes.push({ seat: role, actionId: action.id, status: cancel ? 'CANCELLED' : 'BLOCKED', reason: cancel ? 'The Lead called a Time-Out.' : 'The patient has gone, or the receiving team no longer agrees.' });
        return false;
      }
      if (freeBedIndex(ctx.s, t, to) < 0 && (to === 'ICU' || consent.mode === 'BED')) return true;
      if (to === 'ICU' && t.board.vents && diag(p.believedKey).vent && ventsFree(ctx.s, t) <= 0) return true;
      const outcome = resolveAction(ctx, t, role, action);
      outcomes.push(outcome);
      if (outcome.status !== 'APPLIED') return false;
      p.status = 'INCOMING';
      t.pendingTransfers.push({ patient: p.id, from: role, to, turn: ctx.s.clock.turn });
      emit(ctx, { type: 'TRANSFER_RESERVED', table: t.no, seat: to, patient: p.id, text: `${p.name}: ${to} accepted the patient for next turn${p.loc.kind === 'corridor' ? ', even without a free bed' : ''}.`, audience: ['faculty', `seat:${role}`, `seat:${to}`] });
      return false;
    });
    if (pending.length === before) break;
  }
  for (const { role, action } of pending) outcomes.push({ seat: role, actionId: action.id, status: 'BLOCKED', reason: 'No bed became free for this patient; they stay with the sending team.' });
  return outcomes;
}

/** Deliver reserved patients without resetting their hospital arrival age. */
export function releaseTransfers(ctx: Ctx): void {
  for (const t of ctx.s.tables) {
    for (const transfer of t.pendingTransfers) {
      const p = ctx.s.patients[transfer.patient];
      if (!p || p.status !== 'INCOMING') continue;
      p.status = 'ACTIVE';
      emit(ctx, { type: 'TRANSFER_ARRIVED', table: t.no, seat: p.seat, patient: p.id, text: `${p.name} arrived from ${transfer.from}; they need care this turn.`, audience: ['faculty', `seat:${transfer.from}`, `seat:${transfer.to}`] });
    }
    t.pendingTransfers = [];
  }
}
